import { useMemo } from 'react'
import { Outlet, useOutletContext } from 'react-router-dom'
import { useNavigate } from 'react-router-dom'

import { Box } from '@factorialco/deprecated-design-system'
import { OneDataCollection } from '@factorialco/f0-react/dist/experimental'
import { Add, Download } from '@factorialco/f0-react/icons/app'
import { ExpensablesDataCollectionDocument } from 'generated/resources/graphql'

import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useGraphqlDataCollectionSource } from 'modules/core/hooks/useGraphqlDataCollectionSource'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useExpensesNavigation } from 'modules/expenses/hooks/useExpensesNavigation'
import type { Group } from 'modules/expenses/hooks/useGroup'
import { useExportPdf } from 'modules/expenses/hooks/useGroup'
import { usePageDateFilters } from 'modules/expenses/lib/PageDateFiltersContext'
import { type RouteContextParams } from 'modules/expenses/lib/RouteContextParams'
import {
  EXPENSE_SUMMARIES_CONFIG,
  formatExpenseSummary,
} from 'modules/expenses/lib/expensables/formatExpenseSummary'
import { useExpenses } from 'modules/expenses/pages/ExpensesContext'
import {
  SyncFooter,
  useDataCollectionSyncAction,
  useIntegrationsSyncContext,
} from 'modules/integrations/public'

import { useExpensesBulkActions } from './bulkActions'
import { mapFiltersToVariables } from './filters'
import { useExpenseItemActions } from './itemActions'
import { useExpensesPresetsConfig } from './useExpensesPresetsConfig'
import { useExpensablesStatusCounts } from './useExpensablesStatusCounts'
import { useExpensesDataCollection } from './useExpensesDataCollection'
import { useExpensesFilters } from './useExpensesFilters'

interface Props {
  companyId: number
  canSeeTransactions: boolean
  currentPayrollPeriodOpen: boolean
  availablePayrollCategories: string[]
  back: { href: string }
  onAddUsers?: () => void
  group?: Group
  recommendationsEnabled?: boolean
}

export const ExpensesDataCollection = ({
  companyId,
  canSeeTransactions,
  currentPayrollPeriodOpen,
  availablePayrollCategories,
  back,
  onAddUsers,
  group,
  recommendationsEnabled,
}: Props) => {
  const context = useOutletContext<RouteContextParams>()
  const {
    access: { locale },
  } = useCurrentSession()

  const navigate = useNavigate()
  const { navigation } = useExpensesNavigation()
  const { data: expensesData, featureAvailabilities } = useExpenses()
  const { exportPdf, loading: exportingPdf } = useExportPdf()

  const { dateFilters, setPageDateFilters } = usePageDateFilters()
  const filterDefinitions = useExpensesFilters({
    companyId,
    isGroupView: !!group,
    recommendationsEnabled,
    integrationsFrameworkExpensesSyncFeatureAvailable:
      featureAvailabilities.integrationsFrameworkExpensesSync,
  })

  const statusCounts = useExpensablesStatusCounts({
    companyId,
    groupId: group?.id,
    includeManualDrafts: false,
    from: !group ? dateFilters.startDate : undefined,
    to: !group ? dateFilters.endDate : undefined,
  })

  const {
    navigationFilters: navigationFiltersDefinitions,
    presets: presetsDefinitions,
    presetsLoading,
  } = useExpensesPresetsConfig({ includeDraft: false, statusCounts, showDateNavigation: !group })

  const { sortings, defaultSortings, columns } = useExpensesDataCollection({
    locale,
    customizationFields: expensesData.expenses.customizationFields ?? [],
    showGroupColumn: !group,
  })
  const expensePermissions = useMemo(
    () => ({
      canSyncWithPayroll: context.canSyncWithPayroll,
      canSyncWithBanking: context.canSyncWithBanking,
      canRestartApprovalFlow: expensesData.expenses._policies.expenseRestartApprovalFlow.can,
      canSetExpensableToPaid: expensesData.expenses._policies.expenseSetToPaid.can,
      section: context.section,
    }),
    [context, expensesData.expenses._policies]
  )

  const { bulkActions, handleBulkAction, Modals, handlers, modalActions } = useExpensesBulkActions({
    ...expensePermissions,
    currentPayrollPeriodOpen,
    availablePayrollCategories,
  })

  const { getItemActions } = useExpenseItemActions({
    ...expensePermissions,
    handlers,
    modalActions,
    navigationHandler: {
      navigate,
      navigation,
    },
    isViewingGroup: !!group,
  })

  const { syncEnabled, marketplaceIntegration, setDataCollectionSource } =
    useIntegrationsSyncContext()

  const hiddenSyncAction =
    !syncEnabled || !!group || !featureAvailabilities.integrationsFrameworkExpensesSync

  const { dataCollectionSyncActions, syncModal } = useDataCollectionSyncAction({
    disabled: hiddenSyncAction,
  })

  const handleExportPDF = async () => {
    if (!group) return

    const handled = handleResult(await exportPdf(group.id, companyId), () => null)
    if (handled.success) {
      app.setFlash({
        type: 'correct',
        message: i18n.t('expenses_v2.group_detail.flash_messages.export_pdf.success'),
      })
    } else {
      app.setFlash({
        type: 'wrong',
        message: i18n.t('expenses_v2.group_detail.flash_messages.export_pdf.error'),
      })
    }
  }

  const source = useGraphqlDataCollectionSource(
    {
      document: ExpensablesDataCollectionDocument,
      mapData: (data) => data.expenses.expensablesConnection.nodes,

      mapVariables: ({ navigationFilters, search, sortings: mapSortings, filters }) => {
        const { status, employeeIds, grouped, filter, integrationsSyncableStates } =
          mapFiltersToVariables(filters, {
            isGroupView: !!group,
          })

        return {
          companyId: companyId,
          companyIdString: companyId.toString(),
          sortOrder: mapSortings?.[0],
          onlyGrouped: grouped,
          includeGrouped: grouped !== false,
          includeManualDrafts: false,
          includeSyncableState: syncEnabled,
          integrationsSyncableStates,
          marketplaceIntegrationId: syncEnabled ? marketplaceIntegration?.id : undefined,
          search,
          from: !group ? navigationFilters?.date?.value?.from : undefined,
          to: !group ? navigationFilters?.date?.value?.to : undefined,
          groupIds: group ? [group.id] : undefined,
          status,
          employeeIds,
          filter,
        }
      },
      paginationType: 'infinite-scroll',
      perPage: 100, // TODO: Change when F0 fixes the issue with the infinite scroll https://github.com/factorialco/f0/pull/2839
      mapPagination: (data) => ({
        total: data.expenses.expensablesConnection.totalCount ?? 0,
        cursor: data.expenses.expensablesConnection.pageInfo.endCursor ?? undefined,
        hasNextPage: data.expenses.expensablesConnection.pageInfo.hasNextPage ?? false,
      }),
      summaries: EXPENSE_SUMMARIES_CONFIG,
      mapSummaries: (data) => formatExpenseSummary(data.expenses.expensablesConnection, locale),

      navigationFilters: navigationFiltersDefinitions,
      search: {
        enabled: true,
      },
      filters: filterDefinitions,
      presets: presetsDefinitions,
      presetsLoading,
      sortings,
      defaultSortings,
      itemUrl: ({ id }) => navigation.helpers.expensableDetail(id),
      itemActions: getItemActions,
      primaryActions: () => [
        ...(!syncEnabled && !group
          ? [
              {
                label: i18n.t('expenses_v2.buttons.export_expenses'),
                icon: Download,
                onClick: () => {
                  navigate(navigation.exporter.fullPath())
                },
              },
            ]
          : []),
        ...(group
          ? [
              {
                label: i18n.t('expenses_v2.buttons.export_to_pdf'),
                icon: Download,
                onClick: () => !exportingPdf && handleExportPDF(),
              },
            ]
          : []),
      ],
      secondaryActions: {
        expanded: group ? 0 : 1,
        actions: () => [
          ...dataCollectionSyncActions,
          ...(syncEnabled && !group
            ? [
                {
                  label: i18n.t('expenses_v2.buttons.export_expenses'),
                  icon: Download,
                  onClick: () => {
                    navigate(navigation.exporter.fullPath())
                  },
                },
              ]
            : []),
          ...(onAddUsers
            ? [
                {
                  label: i18n.t('billing.subscription.plans.expenses.actions.add_users'),
                  icon: Add,
                  onClick: onAddUsers,
                },
              ]
            : []),
        ],
      },
      selectable: (item) => item.id,
      bulkActions,
    },
    [syncEnabled, marketplaceIntegration?.id]
  )

  setDataCollectionSource(source)

  return (
    <>
      <Modals />
      {syncModal}
      <Outlet context={{ ...context, back, canSeeTransactions, dataCollectionEnabled: true }} />
      <Box flexDirection="column" height="full">
        <Box flexGrow paddingX="s12" style={{ overflow: 'auto' }}>
          <OneDataCollection
            id={group ? `expenses/group-${group.id}/v2` : 'expenses/v2'}
            storage={{
              features: ['filters', 'sortings', 'search'],
            }}
            fullHeight
            emptyStates={{
              'no-data': {
                title: i18n.t('expenses_v2.list.empty_state.no_expenses'),
                description: '',
              },
            }}
            source={source}
            onBulkAction={handleBulkAction}
            onStateChange={(state) => {
              // Sync date filters with page-level state (only for non-group views)
              if (!group) {
                const { from, to } = state.navigationFilters?.date?.value || {}
                if (from && to) {
                  if (
                    dateFilters.startDate.getTime() !== from.getTime() ||
                    dateFilters.endDate.getTime() !== to.getTime()
                  ) {
                    setPageDateFilters(from, to)
                  }
                }
              }
            }}
            visualizations={[
              {
                type: 'table',
                options: {
                  allowColumnHiding: true,
                  allowColumnReordering: true,
                  columns,
                },
              },
            ]}
          />
        </Box>
        {!hiddenSyncAction && <SyncFooter />}
      </Box>
    </>
  )
}
